import { debounce } from '@tanstack/pacer'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Input } from '@/components/base/input'
import { useI18n } from '@/i18n/i18n-react'

export const SearchField = ({
	accessibleLabel,
	placeholder,
	/**
	 * Search param this field drives. Defaults to `q`. A page that shows a list and a detail pane side
	 * by side needs two independent searches, so the list panel passes its own name (e.g. `listQ`) to
	 * keep it from filtering the detail pane's own `q`-backed table.
	 */
	paramName = 'q',
	/** Whether a new search term resets `offset`. Off for a list that owns pagination locally. */
	resetOffset = true
}: {
	accessibleLabel?: string
	placeholder?: string
	paramName?: string
	resetOffset?: boolean
}) => {
	const { L } = useI18n()
	const navigate = useNavigate()
	const currentSearch = useSearch({ strict: false }) as Record<string, unknown>
	const currentValue = (currentSearch[paramName] as string | undefined) || ''

	const [searchTerm, setSearchTerm] = useState(currentValue)

	// Tracks the last value we sent to the URL via the debounced navigate.
	// This is used to distinguish our own URL updates from external ones (e.g. back button).
	// Without this, a race condition occurs:
	//   1. User types "a" -> searchTerm="a", debounce scheduled
	//   2. 300ms later, debounce fires -> URL updates to q="a"
	//   3. User types "b" before the URL update triggers a re-render -> searchTerm="ab"
	//   4. URL update from step 2 arrives -> currentSearch.q="a"
	//   5. useEffect sees "a" !== "ab" and overwrites searchTerm back to "a", deleting "b"
	// By tracking what we navigated to, the effect can ignore URL changes that originated
	// from our own debounce and only sync on truly external changes.
	const lastNavigatedValue = useRef(currentValue)

	// Debounced function to update search params
	const debouncedSetSearch = useMemo(
		() =>
			debounce(
				(newSearchTerm: string) => {
					lastNavigatedValue.current = newSearchTerm
					navigate({
						to: '.',
						search: (prev: any) => ({
							...prev,
							[paramName]: newSearchTerm || undefined,
							...(resetOffset ? { offset: 0 } : {})
						})
					})
				},
				{ wait: 300 }
			),
		[navigate, paramName, resetOffset]
	)

	// Update local state on input change
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value)
		debouncedSetSearch(event.target.value) // Trigger debounced update
	}

	// Sync local state with URL only if search param changes externally (e.g., back button)
	useEffect(() => {
		if (currentValue !== lastNavigatedValue.current) {
			lastNavigatedValue.current = currentValue
			setSearchTerm(currentValue)
		}
	}, [currentValue])

	return (
		<div className='w-auto'>
			<Input
				size='base'
				type='search'
				value={searchTerm}
				onChange={handleChange}
				placeholder={placeholder ?? L.General.SearchPlaceholder()}
				// Always expose an explicit accessible name. Callers should pass a descriptive
				// accessibleLabel; when omitted we fall back to the localized placeholder so the search
				// input is never rendered without an accessible name.
				aria-label={accessibleLabel ?? placeholder ?? L.General.SearchPlaceholder()}
			/>
		</div>
	)
}
